import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Plus, Heart, User, LogIn, LogOut, ListMusic, Download } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const Header = ({ onRecordClick }) => {
  const { user, login, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBtn, setShowInstallBtn] = useState(false);

  // PWA Install prompt handler
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBtn(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallBtn(false);
    }
    
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowInstallBtn(false);
    }
    setInstallPrompt(null);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="neumorphic-header sticky top-0 z-50">
      <div className="max-w-4xl mx-auto px-2 sm:px-4 py-2">
        {/* Main row - Logo + Auth */}
        <div className="flex items-center justify-between mb-2">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-1.5" data-testid="logo">
            <div className="w-8 h-8 rounded-lg bg-audioyou-accent flex items-center justify-center">
              <span className="text-white text-base">🎙️</span>
            </div>
            <span className="text-lg font-bold text-gray-800">AudioToo</span>
          </Link>

          {/* Auth + Install */}
          <div className="flex items-center gap-2">
            {showInstallBtn && (
              <button
                onClick={handleInstall}
                className="neumorphic-button-small p-2"
                title="Instalar app"
                data-testid="install-pwa-btn"
              >
                <Download size={18} className="text-audioyou-accent" />
              </button>
            )}
            
            {isAuthenticated ? (
              <button
                onClick={logout}
                className="neumorphic-button-small p-2 text-red-500"
                title="Cerrar sesión"
                data-testid="logout-btn"
              >
                <LogOut size={18} />
              </button>
            ) : (
              <button
                onClick={login}
                className="neumorphic-button-accent px-3 py-1.5 flex items-center gap-1.5 text-sm"
                data-testid="login-btn"
              >
                <LogIn size={16} />
                <span>Entrar</span>
              </button>
            )}
          </div>
        </div>

        {/* Navigation row - Always visible, no hamburger */}
        <nav className="flex items-center justify-center gap-1 flex-wrap">
          <Link
            to="/dashboard"
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-all ${
              isActive('/dashboard')
                ? 'bg-audioyou-accent text-white'
                : 'neumorphic-button-small'
            }`}
            data-testid="nav-feed"
          >
            <Home size={16} />
            <span>Feed</span>
          </Link>

          <Link
            to="/combos"
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-all ${
              isActive('/combos')
                ? 'bg-audioyou-accent text-white'
                : 'neumorphic-button-small'
            }`}
            data-testid="nav-combos"
          >
            <ListMusic size={16} />
            <span>Combos</span>
          </Link>

          {isAuthenticated && (
            <>
              <button
                onClick={onRecordClick}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm bg-green-500 text-white"
                data-testid="record-audio-btn"
              >
                <Plus size={16} />
                <span>Grabar</span>
              </button>

              <Link
                to="/favorites"
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-all ${
                  isActive('/favorites')
                    ? 'bg-audioyou-accent text-white'
                    : 'neumorphic-button-small'
                }`}
                data-testid="nav-favorites"
              >
                <Heart size={16} />
                <span>Guardados</span>
              </Link>

              <Link
                to="/profile"
                className={`flex items-center gap-1 px-2 py-1.5 rounded-full text-sm transition-all ${
                  isActive('/profile')
                    ? 'bg-audioyou-accent text-white'
                    : 'neumorphic-button-small'
                }`}
                data-testid="nav-profile"
              >
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt="Profile" 
                    className="w-5 h-5 rounded-full"
                  />
                ) : (
                  <User size={16} />
                )}
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
